import { DialogComponent } from '@theme/dialog';
import { fetchConfig } from '@theme/utilities';
import { CURRENCY_DECIMALS, DEFAULT_CURRENCY_DECIMALS } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/**
 * Updates the "+N" more-count to reflect how many items are hidden on the current screen
 * (based on visible thumbnails, since CSS hides some at different breakpoints).
 * @param {HTMLElement} block - The [data-shop-the-look] block element
 */
function updateMoreCount(block) {
  const totalCount = parseInt(block.dataset.totalCount ?? '', 10);
  if (Number.isNaN(totalCount)) return;

  const thumbWraps = block.querySelectorAll('.shop-the-look__thumb-wrap');
  let visibleCount = 0;
  thumbWraps.forEach((el) => {
    const htmlEl = el instanceof HTMLElement ? el : null;
    if (!htmlEl) return;
    const style = getComputedStyle(htmlEl);
    if (style.display !== 'none' && style.visibility !== 'hidden') {
      visibleCount += 1;
    }
  });

  const moreCount = Math.max(0, totalCount - visibleCount);
  const countEl = block.querySelector('.shop-the-look__more-count');
  const moreIndicator = block.querySelector('.shop-the-look__more-indicator');
  const dialogWrap = block.querySelector('shop-the-look-dialog');

  if (countEl) countEl.textContent = '+' + moreCount;
  if (moreIndicator instanceof HTMLElement) moreIndicator.style.display = moreCount > 0 ? '' : 'none';
  if (dialogWrap instanceof HTMLElement) dialogWrap.style.display = moreCount > 0 ? '' : 'none';
}

/**
 * Format cents for money display (thousands/decimal separators).
 * @param {number} moneyValue - Amount in cents
 * @param {string} thousandsSeparator
 * @param {string} decimalSeparator
 * @param {number} precision
 * @returns {string}
 */
function formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
  const roundedNumber = (moneyValue / 100).toFixed(precision);
  let [a, b] = roundedNumber.split('.');
  if (!a) a = '0';
  if (!b) b = '';
  a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);
  return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
}

/**
 * Format money in cents to shop format string (e.g. "${{amount}}").
 * @param {number} totalCents - Total in cents
 * @param {string} formatTemplate - e.g. "${{amount}}" or "{{amount}} USD"
 * @param {string} currency - ISO code
 * @returns {string}
 */
function formatMoneyDisplay(totalCents, formatTemplate, currency) {
  const precision = CURRENCY_DECIMALS[currency?.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;
  return (formatTemplate || '{{amount}}').replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
    if (placeholder === 'currency') return currency || '';
    if (placeholder === 'amount_no_decimals') return formatCents(totalCents, ',', '.', 0);
    if (placeholder === 'amount_with_comma_separator') return formatCents(totalCents, '.', ',', precision);
    if (placeholder === 'amount_no_decimals_with_comma_separator') return formatCents(totalCents, '.', ',', 0);
    if (placeholder === 'amount_no_decimals_with_space_separator') return formatCents(totalCents, ' ', '.', 0);
    if (placeholder === 'amount_with_space_separator') return formatCents(totalCents, ' ', ',', precision);
    if (placeholder === 'amount_with_period_and_space_separator') return formatCents(totalCents, ' ', '.', precision);
    if (placeholder === 'amount_with_apostrophe_separator') return formatCents(totalCents, "'", '.', precision);
    return formatCents(totalCents, ',', '.', precision);
  });
}

/**
 * Dialog for "Shop the Look" block. Uses theme dialog (focus trap, ESC close).
 * Updates grand total from selected variants and adds selected variants to cart.
 * @extends DialogComponent
 */
class ShopTheLookDialog extends DialogComponent {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {(() => void) | null} */
  #selectChangeBound = null;

  connectedCallback() {
    super.connectedCallback();
    const block = this.closest('[data-shop-the-look]');
    if (!(block instanceof HTMLElement)) return;

    const update = () => updateMoreCount(block);
    update();
    this.#resizeObserver = new ResizeObserver(update);
    this.#resizeObserver.observe(block);

    this.addEventListener('click', this.#handleDelegatedClick);
    this.addEventListener('dialog:close', this.#handleClose);

    const content = this.querySelector('.shop-the-look-modal__content');
    if (content instanceof HTMLElement) {
      this.#bindModalContent(content);
    }
  }

  /**
   * @param {HTMLElement} content
   */
  #bindModalContent(content) {
    const selects = content.querySelectorAll('[data-shop-the-look-select]');
    const totalEl = content.querySelector('[data-shop-the-look-total]');
    const inner = content.closest('.shop-the-look-modal__inner');
    const addBtn = inner?.querySelector('[data-shop-the-look-add]');


    const updateTotalAndButton = () => {
      let totalCents = 0;
      selects.forEach((select) => {
        if (!(select instanceof HTMLSelectElement)) return;
        const value = select.value?.trim();
        if (!value) return;
        const selectedOption = select.options[select.selectedIndex];
        const variantPrice = selectedOption?.dataset?.variantPrice;
        if (variantPrice !== undefined) {
          const cents = Number(variantPrice);
          if (!Number.isNaN(cents)) totalCents += cents;
        }
      });
      if (totalEl instanceof HTMLElement) {
        const format = content.dataset.moneyFormat || '{{amount}}';
        const currency = content.dataset.currency || '';
        totalEl.textContent = formatMoneyDisplay(totalCents, format, currency);
      }
      const hasSelection = totalCents > 0;
      if (addBtn instanceof HTMLButtonElement) {
        addBtn.disabled = !hasSelection;
        addBtn.setAttribute('aria-disabled', String(!hasSelection));
      }
    };

    this.#selectChangeBound = updateTotalAndButton;
    /** @param {Element} select */
    selects.forEach((select) => select.addEventListener('change', updateTotalAndButton));

    if (addBtn) {
      addBtn.addEventListener('click', this.#handleAddToCart);
    }

    this.#bindSizeDropdowns(content);
    updateTotalAndButton();
  }

  /**
   * Binds custom size dropdowns: trigger opens panel, cell selects variant, close button, system tabs.
   * @param {HTMLElement} content
   */
  #bindSizeDropdowns(content) {
    const dropdowns = content.querySelectorAll('[data-shop-the-look-size-dropdown]');
    dropdowns.forEach((wrap) => {
      if (!(wrap instanceof HTMLElement)) return;
      const trigger = wrap.querySelector('[data-shop-the-look-size-trigger]');
      const panel = wrap.querySelector('[data-shop-the-look-size-panel]');
      const select = wrap.querySelector('[data-shop-the-look-select]');
      const triggerText = wrap.querySelector('[data-shop-the-look-size-trigger-text]');
      const closeBtn = wrap.querySelector('[data-shop-the-look-size-close]');
      const cells = wrap.querySelectorAll('[data-shop-the-look-size-cell]');
      const tabs = wrap.querySelectorAll('[data-shop-the-look-size-tab]');

      const closePanel = () => {
        wrap.classList.remove('is-open');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        if (panel) panel.hidden = true;
      };

      const openPanel = () => {
        wrap.classList.add('is-open');
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
        if (panel) panel.hidden = false;
      };

      const syncTriggerText = () => {
        if (!triggerText || !(select instanceof HTMLSelectElement)) return;
        const opt = select.options[select.selectedIndex];
        triggerText.textContent = opt ? opt.textContent?.trim() || triggerText.dataset.placeholder || '' : (triggerText.dataset.placeholder || '');
      };

      if (trigger) {
        trigger.addEventListener('click', () => {
          if (panel?.hidden) openPanel();
          else closePanel();
        });
      }

      if (closeBtn) closeBtn.addEventListener('click', closePanel);

      if (triggerText) triggerText.dataset.placeholder = triggerText.textContent || '';

      cells.forEach((cell) => {
        if (!(cell instanceof HTMLElement)) return;
        cell.addEventListener('click', () => {
          const vid = cell.dataset.variantId;
          if (vid && select instanceof HTMLSelectElement) {
            select.value = vid;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            syncTriggerText();
            closePanel();
          }
        });
      });

      tabs.forEach((tab) => {
        if (!(tab instanceof HTMLElement)) return;
        tab.addEventListener('click', () => {
          tabs.forEach((t) => {
            if (t instanceof HTMLElement) {
              t.classList.toggle('shop-the-look-size-dropdown__system-tab--active', t === tab);
              t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
            }
          });
          const system = tab.dataset.sizeSystem?.toLowerCase();
          if (!system) return;
          cells.forEach((cell) => {
            if (!(cell instanceof HTMLElement)) return;
            try {
              const sizes = cell.dataset.sizes ? JSON.parse(cell.dataset.sizes) : {};
              const val = sizes[system];
              cell.textContent = val !== undefined && val !== null ? String(val) : '';
            } catch (_) {}
          });
        });
      });

      syncTriggerText();
    });
  }

  /** @param {MouseEvent} e */
  #handleAddToCart = async (e) => {
    const btn = e.currentTarget;
    if (!(btn instanceof HTMLButtonElement) || btn.disabled) return;

    const content = this.querySelector('.shop-the-look-modal__content');
    if (!(content instanceof HTMLElement)) return;

    const selects = content.querySelectorAll('[data-shop-the-look-select]');
    /** @type {{ id: number; quantity: number }[]} */
    const items = [];
    selects.forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;
      const value = select.value?.trim();
      if (value) items.push({ id: Number(value), quantity: 1 });
    });
    if (items.length === 0) return;

    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');

    try {
      const body = JSON.stringify({ items });
      const cartAddUrl = typeof Theme !== 'undefined' && Theme?.routes?.cart_add_url ? Theme.routes.cart_add_url : '/cart/add.js';
      const res = await fetch(cartAddUrl, {
        ...fetchConfig('json', { body }),
        method: 'POST',
      });
      const data = await res.json();

      if (data.status && data.status !== 200) {
        if (typeof data.message === 'string') {
          const totalEl = content.querySelector('[data-shop-the-look-total]');
          if (totalEl) totalEl.textContent = data.message;
        }
        return;
      }

      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      const itemCount = cart.item_count ?? 0;

      document.dispatchEvent(
        new CartAddEvent({}, this.id || 'shop-the-look', {
          source: 'shop-the-look',
          itemCount,
        })
      );

      this.closeDialog();
    } catch (err) {
      console.error('Shop the look add to cart:', err);
    } finally {
      const inner = this.querySelector('.shop-the-look-modal__inner');
      const addBtnAgain = inner?.querySelector('[data-shop-the-look-add]');
      if (addBtnAgain instanceof HTMLButtonElement) {
        addBtnAgain.disabled = false;
        addBtnAgain.setAttribute('aria-disabled', 'false');
      }
    }
  };

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.removeEventListener('dialog:close', this.#handleClose);
    this.removeEventListener('click', this.#handleDelegatedClick);
    const content = this.querySelector('.shop-the-look-modal__content');
    const bound = this.#selectChangeBound;
    if (content && bound) {
      const selects = content.querySelectorAll('[data-shop-the-look-select]');
      selects.forEach((select) => select.removeEventListener('change', bound));
    }
    const addBtn = content?.querySelector('[data-shop-the-look-add]');
    if (addBtn) addBtn.removeEventListener('click', this.#handleAddToCart);
    super.disconnectedCallback();
  }

  #handleTriggerClick = (/** @type {Event} */ e) => {
    e.preventDefault();
    this.showDialog();
    const trigger = this.closest('[data-shop-the-look]')?.querySelector('[data-shop-the-look-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  };

  #handleDelegatedClick = (/** @type {Event} */ e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest('[data-shop-the-look-trigger]');
    if (!trigger || !this.contains(trigger)) return;
    this.#handleTriggerClick(e);
  };

  #handleClose = () => {
    const trigger = this.closest('[data-shop-the-look]')?.querySelector('[data-shop-the-look-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  };
}

if (!customElements.get('shop-the-look-dialog')) {
  customElements.define('shop-the-look-dialog', ShopTheLookDialog);
}
